import { useEffect, useMemo, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { FormikProvider, useFormik } from 'formik'
import { array, object } from 'yup'
import { fa2, faPercent, faTrashCan } from '@fortawesome/pro-regular-svg-icons'
import { EMPTY_ALLOCATION_RULE } from './AllocationConstants'
import { AllocationRule } from '@/src/reducers/finances/allocationRulesReducer'
import {
  Alert,
  Button,
  Card,
  Separator,
  Text,
  Form,
  Input,
  CardColors,
  Banner,
  BannerVariants,
  Icon,
} from '@/ui'
import { useAnalyticsTrack } from '@/features/amplitude'
import { TextColors, TypographyStyles } from '@/styles'

// TODO: Move to V2
import {
  makeNumberSchema,
  makeReqStringSchema,
} from '@/componentsV1/BaseComponents/FormInputs'

const validationSchema = object({
  rules: array().of(
    object({
      ruleName: makeReqStringSchema({ field: 'allocation name' }),
      percentage: makeNumberSchema({
        field: 'Percentage',
        allowedDecimals: 2,
      }),
    })
  ),
})

interface Props {
  initialRules: AllocationRule[]
  percentageSum: number
  progressForward: () => void
  updateRules: (rule: AllocationRule[]) => void
}

export const AllocationRuleForm = ({
  initialRules,
  updateRules,
  progressForward,
  percentageSum,
}: Props) => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const isOnboarding = searchParams.get('onboarding') === 'true'
  const scrollRef = useRef<HTMLSpanElement>(null)

  const hasPercentageError = useMemo(
    () => Number(percentageSum) > 100,
    [percentageSum]
  )

  const track = useAnalyticsTrack()

  const formik = useFormik<{ rules: (AllocationRule & { hint?: string })[] }>({
    enableReinitialize: true,
    initialValues: {
      rules: initialRules,
    },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async (values, formikHelpers) => {
      track('clicked review on allocation guide edit')
      const errors = await formikHelpers.validateForm(values)

      if ((!errors.rules || errors.rules.length === 0) && !hasPercentageError) {
        progressForward()
      }

      return
    },
  })

  useEffect(() => {
    updateRules(formik.values.rules)
  }, [formik.values.rules, updateRules])

  const handleRemove = (idx: number) => {
    const newRules = [...formik.values.rules]
    newRules.splice(idx, 1)
    formik.setFieldValue('rules', newRules)
    if (updateRules) {
      updateRules(newRules)
    }
  }

  return (
    <FormikProvider value={formik}>
      <Card color={CardColors.LEVEL_0} className="mt-6 space-y-6">
        <Text
          variant={TypographyStyles.H2}
          className="mt-4 mb-4 inline-flex items-center"
        >
          <Icon
            icon={fa2}
            backgroundColor="level2"
            backgroundStyle="circle"
            size="1x"
          />
          Set your monthly allocations
        </Text>
        <Banner
          variant={BannerVariants.DEFAULT}
          wrapperClassName="w-full justify-start"
          text="This is based off your monthly profit, or what's left of your earnings once you've paid your expenses for the month."
        />
        <span ref={scrollRef} />

        {/* Form-level Error */}
        {(formik.errors.rules?.length ?? 0) > 0 || hasPercentageError ? (
          <Alert
            variant="error"
            description="Please correct the errors in your form!"
          />
        ) : null}

        {/* Table Header */}
        <div className="grid grid-cols-1 md:grid-cols-12 items-center my-4">
          <div className="md:col-span-8">
            <Text
              variant={TypographyStyles.EYEBROW}
              color={TextColors.EMPHASIS}
            >
              Allocation Type
            </Text>
          </div>
          <div className="md:col-span-4">
            <Text
              variant={TypographyStyles.EYEBROW}
              color={TextColors.EMPHASIS}
            >
              Percentage of Profit to Allocate
            </Text>
          </div>
        </div>

        {/* Empty State */}
        {formik.values.rules.length === 0 && (
          <Alert
            variant="error"
            description="Please add Allocations here in order to build your Allocation Guide!"
          />
        )}

        {/* Allocation Rows */}
        {formik.values.rules.map((rule, idx) => (
          <div
            key={`form-rule-row-${idx}`}
            className="grid grid-cols-1 md:grid-cols-12 gap-4 items-start"
          >
            <div className="md:col-span-8">
              <Form.Input
                className="w-full"
                name={`rules[${idx}].ruleName`}
                placeholder="Fill in a name for this allocation"
              />
              {rule.hint && (
                <Text
                  variant={TypographyStyles.BODY_SM}
                  color={TextColors.MUTED}
                  className="mt-1"
                >
                  {rule.hint}
                </Text>
              )}
            </div>
            <div className="md:col-span-3">
              <Form.Input
                className="w-full text-right"
                name={`rules[${idx}].percentage`}
                placeholder="Enter percentage"
                iconRight={faPercent}
                step="1"
                type="number"
              />
            </div>
            <div className="md:col-span-1 flex items-center justify-end">
              <Button
                onClick={() => handleRemove(idx)}
                size="square"
                variant="ghost"
                iconLeft={faTrashCan}
              />
            </div>
          </div>
        ))}

        {/* Add Row Button */}
        <div className="flex justify-end">
          <Button
            variant="secondary"
            onClick={() => {
              formik.setFieldValue('rules', [
                ...formik.values.rules,
                EMPTY_ALLOCATION_RULE,
              ])
              formik.validateForm()
            }}
          >
            Add additional Allocations
          </Button>
        </div>

        {/* Total Percentage Row */}
        <div className="grid grid-cols-1 md:grid-cols-12 items-center">
          <div className="md:col-span-8" />
          <div className="md:col-span-4">
            <Text variant={TypographyStyles.H3} className="mb-2">
              <b>Total % of Profit Allocated</b>
            </Text>
            <Input
              className="w-[100px]"
              disabled
              iconRight={faPercent}
              value={String(percentageSum)}
              readOnly={Boolean(percentageSum)}
              name="value"
            />
            {hasPercentageError && (
              <Alert
                variant="error"
                description="Your total percentage allocated is greater than 100%"
                className="mt-2"
              />
            )}
          </div>
        </div>
      </Card>

      <Separator className="text-neutral-200" />

      <div className="flex justify-between items-center">
        <Button
          variant="secondary"
          size="md"
          className="min-w-[200px]"
          onClick={() => navigate(isOnboarding ? '/' : '/allocation-guides')}
        >
          Cancel
        </Button>
        <Button
          disabled={formik.values.rules.length === 0}
          id="btn-allocation-guide-creation-review"
          onClick={() => formik.handleSubmit()}
          type="submit"
          size="md"
          className="min-w-[200px]"
        >
          Review
        </Button>
      </div>
    </FormikProvider>
  )
}
